import { useCallback, useEffect, useRef, useState, useMemo } from 'react'

import { useParams, usePathname } from 'next/navigation'

import { useCommandMenuAvailability } from '~/components/command-menu/hooks/useCommandMenuAvailability'
import {
  CommandMenuContextType,
  type CommandMenuViewOptions,
  type CommandMenuViewType,
} from '~/components/command-menu/types'
import { IAddress } from '~/types'

export const useCommandMenuState = (): {
  menuContext: CommandMenuContextType
  activeView: CommandMenuViewType | null
  pathname: string
  address: IAddress | undefined
  setActiveView: CommandMenuContextType['setActiveView']
  isEnabled: boolean
} => {
  const [activeView, setActiveViewState] = useState<CommandMenuViewType | null>(
    null,
  )
  const [viewHistory, setViewHistory] = useState<CommandMenuViewType[]>([])
  const [searchValue, setSearchValue] = useState('')
  const [overrideContext, setOverrideContextState] = useState(false)

  const viewCallbackRef = useRef<((data: unknown) => void) | null>(null)
  const viewOptionsRef = useRef<CommandMenuViewOptions | null>(null)
  const lastFocusedInputRef = useRef<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null
  >(null)

  const { isEnabled: isCommandMenuEnabled } = useCommandMenuAvailability()
  const closeMenu = useCallback(() => {
    setActiveViewState(null)
    setOverrideContextState(false)
    setViewHistory([])
  }, [])

  const setActiveView = useCallback(
    (
      view: CommandMenuViewType | null,
      callback?: (data: unknown) => void,
      options?: CommandMenuViewOptions,
    ) => {
      if (!isCommandMenuEnabled && view) return
      if (view === null) {
        viewCallbackRef.current = null
        viewOptionsRef.current = null
        closeMenu()
        return
      }

      // saves the last focused input when the command menu is opened
      // that way when we close the command menu, we can re-focus that input
      if (!activeView && view && typeof document !== 'undefined') {
        const activeElement = document.activeElement
        if (
          activeElement instanceof HTMLInputElement ||
          activeElement instanceof HTMLTextAreaElement ||
          activeElement instanceof HTMLSelectElement
        ) {
          lastFocusedInputRef.current = activeElement
        } else {
          lastFocusedInputRef.current = null
        }
      }

      // Push current view to history when navigating to a new view
      if (activeView && view && view !== activeView) {
        setViewHistory((prev) => [...prev, activeView])
      }

      // Auto-wrap callback to close view after execution
      if (callback) {
        const wrappedCallback = (data: unknown) => {
          callback(data)
          closeMenu()
        }
        viewCallbackRef.current = wrappedCallback
      } else {
        viewCallbackRef.current = null
      }

      viewOptionsRef.current = options ?? null
      setActiveViewState(view)
    },
    [activeView, closeMenu, isCommandMenuEnabled],
  )

  const goBack = useCallback(() => {
    if (viewHistory.length > 0) {
      const newHistory = [...viewHistory]
      const previousView = newHistory.pop()!
      setViewHistory(newHistory)
      setActiveViewState(previousView)
    } else if (activeView && activeView !== 'default') {
      setActiveViewState('default')
    } else {
      closeMenu()
    }
  }, [activeView, closeMenu, viewHistory])
  const getViewCallback = useCallback(() => viewCallbackRef.current, [])
  const getViewOptions = useCallback(() => viewOptionsRef.current, [])

  const pathname = usePathname()
  const params = useParams()
  const routeAddress = (params?.account as IAddress) || undefined

  const setOverrideContext = setOverrideContextState

  const effectiveAddress = overrideContext ? undefined : routeAddress

  // clear search when navigating between pages
  useEffect(() => {
    setSearchValue('')
  }, [activeView])

  // focus the last focused input when navigating out of the command menu
  useEffect(() => {
    if (!activeView && lastFocusedInputRef.current) {
      const element = lastFocusedInputRef.current
      lastFocusedInputRef.current = null
      if (typeof element.focus === 'function') {
        requestAnimationFrame(() => {
          if (element.isConnected) {
            try {
              element.focus({ preventScroll: true })
            } catch {
              element.focus()
            }
          }
        })
      }
    }
  }, [activeView])

  useEffect(() => {
    if (!isCommandMenuEnabled && activeView) {
      closeMenu()
    }
  }, [isCommandMenuEnabled, activeView, closeMenu])

  const menuContext = useMemo<CommandMenuContextType>(
    () => ({
      activeView,
      setActiveView,
      goBack,
      searchValue,
      setSearchValue,
      getViewCallback,
      getViewOptions,
      overrideContext,
      setOverrideContext,
    }),
    [
      activeView,
      searchValue,
      overrideContext,
      setActiveView,
      goBack,
      setSearchValue,
      getViewCallback,
      getViewOptions,
      setOverrideContext,
    ],
  )

  return {
    menuContext,
    activeView,
    pathname,
    address: effectiveAddress,
    setActiveView,
    isEnabled: isCommandMenuEnabled,
  }
}
